import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { fetchProductListDataStore } from '../../../../Reducer/dataAdminSlice';
import { Link } from 'react-router-dom';

import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Typography,
  TextField,
  Box,
  Skeleton,
  IconButton,
  Tooltip,
  Pagination,
  useTheme,
  Checkbox,
  Modal,
} from '@mui/material';
import { UploadFile, Visibility } from '@mui/icons-material';
import CloseIcon from '@mui/icons-material/Close';
import { Button } from "@mui/material";
import Emailverifyprocess from './Emailverifyprocess';
import UploadCsv from '../UploadCsv';
import DeleteButton from './DeleteButton';
import EditButtonWithPopup from './EditButtonWithPopup';

const DataStore = () => {
  const dispatch = useDispatch();
  const theme = useTheme();
  const { data, loading, error, pagination } = useSelector((state) => state.dataAdmin);

  const [page, setPage] = useState(0);
  const [rowsPerPage] = useState(10);
  const [filter, setFilter] = useState('');
  const [openPopup, setOpenPopup] = useState(false);
  const [sheetProduct, setSheetProduct] = useState(null);
  const [selectedProducts, setSelectedProducts] = useState([]);
  const [showSelectedData, setShowSelectedData] = useState(false);

  // ✅ Persistent selected state (loaded from localStorage)
  const [selected, setSelected] = useState(() => {
    const saved = localStorage.getItem("selectedProducts");
    return saved ? JSON.parse(saved) : [];
  });

  const handleSelect = (id) => {
    setSelected((prev) => {
      let updated;
      if (prev.includes(id)) {
        updated = prev.filter((item) => item !== id);
      } else {
        updated = [...prev, id];
      }
      localStorage.setItem("selectedProducts", JSON.stringify(updated));
      return updated;
    });
  };

  const handleSelectAll = () => {
    setSelected((prev) => {
      let updated;
      if (prev.length === data.length) {
        updated = [];
      } else {
        updated = data.map((product) => product.id);
      }
      localStorage.setItem("selectedProducts", JSON.stringify(updated));
      return updated;
    });
  };



  useEffect(() => {
    dispatch(fetchProductListDataStore({ page: page + 1, limit: rowsPerPage, event_name: filter }));
  }, [dispatch, page, rowsPerPage, filter]);

  const handleChangePage = (_, newPage) => setPage(newPage);
  const handleFilterChange = (event) => {
    setFilter(event.target.value);
    setPage(0);
  };

  const handleClosePopup = () => setOpenPopup(false);

  // New function to remove product from selectedProducts and selected state
  const handleRemoveProduct = (productId) => {
    setSelectedProducts((prevSelected) => prevSelected.filter((p) => p.id !== productId));
    setSelected((prevSelectedIds) => {
      const updated = prevSelectedIds.filter((id) => id !== productId);
      localStorage.setItem("selectedProducts", JSON.stringify(updated));
      return updated;
    });
  };

  const renderSkeleton = (rows, columns) =>
    Array.from({ length: rows }).map((_, rowIndex) => (
      <TableRow key={rowIndex}>
        {Array.from({ length: columns }).map((_, colIndex) => (
          <TableCell key={colIndex}>
            <Skeleton variant="rectangular" height={30} />
          </TableCell>
        ))}
      </TableRow>
    ));

  return (
    <Box sx={{ p: { xs: 2, sm: 3 }, mt: 2 }}>
      {/* Header Section */}
      <Paper
        elevation={2}
        sx={{
          px: 3,
          py: 2,
          mb: 3,
          borderRadius: 3,
          backgroundColor: '#f8f9fa',
          display: 'flex',
          flexDirection: { xs: 'column', sm: 'row' },
          gap: 2,
          justifyContent: 'space-between',
          alignItems: 'center',
        }}
      >
        <Typography variant="h5" sx={{ fontWeight: 600, color: '#1976d2' }}>
          Product List
        </Typography>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flexDirection: { xs: 'column', sm: 'row' } }}>
          <TextField
            label="Search by Event Name"
            variant="outlined"
            size="small"
            value={filter}
            onChange={handleFilterChange}
            sx={{
              width: { xs: '100%', sm: 300 },
              '& .MuiOutlinedInput-root': {
                borderRadius: 2,
                backgroundColor: '#f9fafb',
                '& fieldset': {
                  borderColor: 'rgba(0,0,0,0.1)',
                },
                '&:hover fieldset': {
                  borderColor: theme.palette.primary.main,
                },
              },
            }}
          />
          <Button
            variant="contained"
            color="primary"
            onClick={() => {
              const selectedProds = data.filter(product => selected.includes(product.id));
              setSelectedProducts(selectedProds);
              setOpenPopup(true);
            }}
          >
            Process to verify Emails
          </Button>
        </Box>
      </Paper>

      {/* Table Section */}
      <TableContainer
        component={Paper}
        sx={{
          borderRadius: 3,
          boxShadow: '0 8px 20px rgba(0,0,0,0.06)',
        }}
      >
        <Table>
          <TableHead>
            <TableRow sx={{ background: 'linear-gradient(to right, #1976d2, #2196f3)' }}>
              {[
                'ID',
                'Select',
                'WooCommerce Product ID',
                'DB Product ID',
                'Event Name',
                'Total Count',
                'Uploaded At',
                'Actions',
                'Update Record',
              ].map((head) => (
                <TableCell
                  key={head}
                  sx={{
                    color: 'white',
                    fontWeight: 'bold',
                    borderBottom: 'none',
                  }}
                >
                  {head === 'Select' ? (
                    <Checkbox
                      color="secondary"
                      checked={selected.length > 0 && selected.length === data.length}
                      indeterminate={selected.length > 0 && selected.length < data.length}
                      onChange={handleSelectAll}
                    />
                  ) : (
                    head
                  )}
                </TableCell>
              ))}
            </TableRow>
          </TableHead>

          <TableBody>
            {loading ? (
              renderSkeleton(10, 9)
            ) : error ? (
              <TableRow>
                <TableCell colSpan={9} align="center">
                  <Typography color="error">{error}</Typography>
                </TableCell>
              </TableRow>
            ) : data.length > 0 ? (
              data.map((product, i) => (
                <TableRow
                  key={product.id}
                  hover
                  sx={{
                    transition: 'background 0.2s ease',
                    '&:hover': { backgroundColor: 'rgba(25, 118, 210, 0.05)' },
                  }}
                >
                  <TableCell>{page * rowsPerPage + i + 1}</TableCell>

                  {/* ✅ Persistent Checkbox */}
                  <TableCell>
                    <Checkbox
                      color="primary"
                      checked={selected.includes(product.id)}
                      onChange={() => handleSelect(product.id)}
                    />
                  </TableCell>

                  <TableCell>{product.woocommerce_product_id}</TableCell>
                  <TableCell>{product.db_product_id || 'N/A'}</TableCell>
                  <TableCell>{product.event_name || 'N/A'}</TableCell>
                  <TableCell>{product.total_count || 'N/A'}</TableCell>
                  <TableCell>{new Date(product.createdAt).toLocaleString()}</TableCell>
                  <TableCell>
                    <Box display="flex" alignItems="center" gap={1}>
                      <Tooltip title="View Data">
                        <IconButton
                          component={Link}
                          to={`/dashboard/data-rows/${product.woocommerce_product_id}/${product.id}`}
                          color="primary"
                        >
                          <Visibility />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Upload sheet">
                        <IconButton color="secondary" onClick={() => setSheetProduct(product)}>
                          <UploadFile />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Delete Record">
                        <DeleteButton record_id={product.id} />
                      </Tooltip>
                    </Box>
                  </TableCell>
                  <TableCell>
                    <EditButtonWithPopup product={product} />
                  </TableCell>
                </TableRow>
              ))
            ) : (
              <TableRow>
                <TableCell colSpan={9} align="center">
                  No data available
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>

        {/* Pagination */}
        <Box
          display="flex"
          justifyContent="space-between"
          alignItems="center"
          px={3}
          py={2}
          borderTop="1px solid rgba(0,0,0,0.05)"
        >
          <Pagination
            count={Math.ceil((pagination?.total_count || 0) / rowsPerPage)}
            page={page + 1}
            onChange={(_, value) => handleChangePage(_, value - 1)}
            color="primary"
            shape="rounded"
          />
          <Typography variant="body2" color="text.secondary">
            Total: <strong>{pagination?.total_count || 0}</strong> records
          </Typography>
        </Box>
      </TableContainer>

      {/* Email verification */}
      {openPopup && (
        <Emailverifyprocess
          open={openPopup}
          onClose={handleClosePopup}
          selectedProducts={selectedProducts}
          onRemoveProduct={handleRemoveProduct}
        />
      )}

      <Modal open={!!sheetProduct} onClose={() => setSheetProduct(null)}>
        <Box
          sx={{
            position: 'absolute',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
            outline: 'none',
          }}
        >
          <IconButton
            onClick={() => setSheetProduct(null)}
            sx={{ position: 'absolute', right: 8, top: 8, zIndex: 1 }}
          >
            <CloseIcon />
          </IconButton>
          {sheetProduct && (
            <UploadCsv
              woocommerce_product_id={sheetProduct.woocommerce_product_id}
              event_name={sheetProduct.event_name}
            />
          )}
        </Box>
      </Modal>
    </Box>
  );
};

export default DataStore;
